// components/MyProfile/Support/ZendeskForm.js (Updated version with KB search)
import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    TextField,
    Button,
    Paper,
    Alert,
    LinearProgress,
    useMediaQuery,
    useTheme,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Divider
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
    HelpCircle,
    Send,
    CheckCircle,
    AlertCircle,
    Paperclip,
    X,
    BookOpen
} from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import zendeskService from '../../../services/zendeskService';
import KnowledgeBaseSearch from './KnowledgeBaseSearch';
import './ZendeskForm.css';

const ZendeskForm = ({ darkMode = false, onSuccess, showKBSearch = true }) => {
    const theme = useTheme();
    const { user, getAccessTokenSilently } = useAuth0();
    
    // Device detection
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    // Form state
    const [formData, setFormData] = useState({
        subject: '',
        category: '',
        priority: 'normal',
        description: '',
        attachments: []
    });
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [ticketId, setTicketId] = useState(null);
    const [showForm, setShowForm] = useState(!showKBSearch);
    
    // Categories for the dropdown
    const categories = [
        { value: 'technical', label: 'Technical Issue' },
        { value: 'billing', label: 'Billing & Payment' },
        { value: 'account', label: 'Account Management' },
        { value: 'circle-management', label: 'Circle Management' },
        { value: 'port-in', label: 'Port-In Process' },
        { value: 'general', label: 'General Inquiry' }
    ];
    
    // Priority options
    const priorities = [
        { value: 'low', label: 'Low', color: '#4CAF50' },
        { value: 'normal', label: 'Normal', color: '#2196F3' },
        { value: 'high', label: 'High', color: '#FF9800' },
        { value: 'urgent', label: 'Urgent', color: '#F44336' }
    ];
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];
        
        const validFiles = files.filter(file => {
            if (file.size > maxSize) {
                setError(`File ${file.name} is too large. Maximum size is 5MB.`);
                return false;
            }
            if (!allowedTypes.includes(file.type)) {
                setError(`File ${file.name} has an unsupported format.`);
                return false;
            }
            return true;
        });
        
        setFormData(prev => ({
            ...prev,
            attachments: [...prev.attachments, ...validFiles].slice(0, 3) // Max 3 files
        }));
    };
    
    const removeAttachment = (index) => {
        setFormData(prev => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index)
        }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);
        
        // Validation
        if (!formData.subject.trim()) {
            setError('Please enter a subject');
            return;
        }
        
        if (!formData.category) {
            setError('Please select a category');
            return;
        }
        
        if (!formData.description.trim()) {
            setError('Please describe your issue');
            return;
        }
        
        try {
            setLoading(true);
            
            // Get Auth0 token
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            // Prepare the ticket data
            const ticketData = {
                ticket: {
                    subject: formData.subject,
                    comment: {
                        body: formData.description
                    },
                    priority: formData.priority,
                    tags: [formData.category, 'web-app'],
                    requester: {
                        name: user?.name || 'User',
                        email: user?.email || ''
                    }
                }
            };
            
            // Create the ticket
            const response = await zendeskService.createTicket(ticketData, token);
            const createdTicketId = response.data.ticket?.id;
            
            if (!createdTicketId) {
                throw new Error('Failed to get ticket ID from response');
            }
            
            // Handle file uploads if any
            if (formData.attachments.length > 0) {
                const uploadFormData = new FormData();
                formData.attachments.forEach(file => {
                    uploadFormData.append('files', file);
                });
                
                try {
                    await zendeskService.uploadAttachments(createdTicketId, uploadFormData, token);
                } catch (uploadError) {
                    console.error('Error uploading attachments:', uploadError);
                    // Don't fail the whole operation if attachments fail
                }
            }
            
            setSuccess(true);
            setTicketId(createdTicketId);
            
            // Call onSuccess callback if provided
            if (onSuccess) {
                onSuccess(createdTicketId);
            }
            
            // Reset form
            setFormData({
                subject: '',
                category: '',
                priority: 'normal',
                description: '',
                attachments: []
            });
            
            // Clear the file input
            const fileInput = document.querySelector('input[type="file"]');
            if (fileInput) fileInput.value = '';
            
        } catch (error) {
            console.error('Error submitting ticket:', error);
            const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message || 
                               'Failed to submit your support request. Please try again.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };
    
    const handleContinueToForm = () => {
        setShowForm(true);
    };
    
    return (
        <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            sx={{ width: '100%' }}
        >
            {/* Show KB Search first if enabled */}
            {showKBSearch && !showForm && (
                <Box>
                    {/* KB Search Section */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: '16px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <BookOpen size={24} color="#2196F3" />
                            <Typography variant="h6">
                                Search our Help Center first
                            </Typography>
                        </Box>
                        <Typography color="textSecondary" sx={{ mb: 3 }}>
                            Many questions can be answered instantly by searching our knowledge base.
                        </Typography>
                        
                        <KnowledgeBaseSearch embedded={true} />
                    </Paper>
                    
                    {/* Continue to form button */}
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography color="textSecondary" sx={{ mb: 2 }}>
                            Can't find what you're looking for?
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleContinueToForm}
                            startIcon={<HelpCircle size={20} />}
                            sx={{
                                bgcolor: '#FF4775',
                                color: 'white',
                                px: 4,
                                py: 1.5,
                                '&:hover': {
                                    bgcolor: '#FF2665'
                                }
                            }}
                        >
                            Submit a Support Ticket
                        </Button>
                    </Box>
                </Box>
            )}
            
            {/* Show form when ready */}
            {(showForm || !showKBSearch) && (
                <>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box sx={{ 
                            width: isMobile ? 64 : 80, 
                            height: isMobile ? 64 : 80, 
                            borderRadius: '50%', 
                            bgcolor: '#FF4775',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            mb: 2
                        }}>
                            <HelpCircle size={isMobile ? 32 : 40} color="white" />
                        </Box>
                        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
                            Submit a Support Ticket
                        </Typography>
                        <Typography color="textSecondary" sx={{ mb: 3 }}>
                            We're here to help! Fill out the form below and we'll get back to you soon.
                        </Typography>
                    </Box>

                    {/* Main Form */}
                    <Paper 
                        component="form" 
                        onSubmit={handleSubmit}
                        sx={{ 
                            p: isMobile ? 3 : 4, 
                            borderRadius: '16px',
                            maxWidth: '800px',
                            mx: 'auto'
                        }}
                        elevation={1}
                    >
                        {/* Status indicators */}
                        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
                        
                        {error && (
                            <Alert 
                                severity="error" 
                                icon={<AlertCircle size={20} />}
                                sx={{ mb: 2 }}
                                onClose={() => setError(null)}
                            >
                                {error}
                            </Alert>
                        )}
                        
                        {success && (
                            <Alert 
                                severity="success" 
                                icon={<CheckCircle size={20} />}
                                sx={{ mb: 2 }}
                            >
                                <Typography variant="subtitle2" gutterBottom>
                                    Your support ticket has been submitted successfully!
                                </Typography>
                                {ticketId && (
                                    <Typography variant="body2">
                                        Ticket ID: #{ticketId}
                                    </Typography>
                                )}
                            </Alert>
                        )}

                        {/* Subject */}
                        <TextField
                            fullWidth
                            label="Subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            disabled={loading}
                            sx={{ mb: 3 }}
                            placeholder="Brief description of your issue"
                        />

                        {/* Category and Priority */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: isMobile ? 'column' : 'row' }}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    label="Category"
                                    disabled={loading}
                                >
                                    {categories.map(cat => (
                                        <MenuItem key={cat.value} value={cat.value}>
                                            {cat.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            <FormControl fullWidth>
                                <InputLabel>Priority</InputLabel>
                                <Select
                                    name="priority"
                                    value={formData.priority}
                                    onChange={handleInputChange}
                                    label="Priority"
                                    disabled={loading}
                                >
                                    {priorities.map(priority => (
                                        <MenuItem key={priority.value} value={priority.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        bgcolor: priority.color
                                                    }}
                                                />
                                                {priority.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Description */}
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            disabled={loading}
                            sx={{ mb: 3 }}
                            placeholder="Please provide as much detail as possible about your issue..."
                        />

                        {/* File Upload */}
                        <Box sx={{ mb: 3 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<Paperclip size={18} />}
                                disabled={loading || formData.attachments.length >= 3}
                                sx={{ 
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                    color: 'text.secondary',
                                    '&:hover': {
                                        borderColor: '#FF4775',
                                        bgcolor: 'rgba(255, 71, 117, 0.04)'
                                    }
                                }}
                            >
                                Attach Files
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    onChange={handleFileUpload}
                                    accept="image/*,.pdf,.txt"
                                />
                            </Button>
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
                                Max 3 files, 5MB each
                            </Typography>
                            
                            {/* Attached Files */}
                            {formData.attachments.length > 0 && (
                                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {formData.attachments.map((file, index) => (
                                        <Chip
                                            key={index}
                                            label={file.name}
                                            onDelete={() => removeAttachment(index)}
                                            size="small"
                                            deleteIcon={<X size={16} />}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            startIcon={loading ? null : <Send size={20} />}
                            sx={{
                                bgcolor: '#FF4775',
                                color: 'white',
                                py: 1.5,
                                fontSize: '1rem',
                                '&:hover': {
                                    bgcolor: '#FF2665'
                                },
                                '&.Mui-disabled': {
                                    bgcolor: 'rgba(255, 71, 117, 0.5)'
                                }
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                    Submitting...
                                </>
                            ) : 'Submit Ticket'}
                        </Button>

                        {/* Help Text */}
                        <Typography 
                            variant="body2" 
                            color="textSecondary" 
                            sx={{ mt: 2, textAlign: 'center' }}
                        >
                            You'll receive a confirmation email at {user?.email}
                        </Typography>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default ZendeskForm;